import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { WaveArt } from '@/components/illustrations';
import { Alert, AlertDescription, Button, Input, Label } from '@/components/cargofl-ui';
import { api, API_URL, ApiError } from '@/lib/api';
import { SESSION_QUERY_KEY } from '@/lib/session';

interface AuthConfig {
  googleClientId: string | null;
  allowedEmailDomain: string | null;
  devLoginEnabled: boolean;
  passwordLoginEnabled: boolean;
  organizationName: string;
}

/**
 * Sign-in, in the split layout the other CargoFL apps use.
 *
 * Left is the task and nothing else. Right is the dark teal panel saying what this
 * system is - it is the first screen a new joiner sees, and three lines there save
 * a conversation later.
 */
export function LoginPage() {
  const queryClient = useQueryClient();
  const { data: config, isPending, isError } = useQuery({
    queryKey: ['auth', 'config'],
    queryFn: () => api.get<AuthConfig>('/auth/config'),
    retry: 1,
  });

  const onSignedIn = React.useCallback(async () => {
    // Refetch rather than writing the response into the cache by hand: /auth/me is
    // the single source of who is signed in, and two ways of populating it is how
    // they start disagreeing.
    await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
  }, [queryClient]);

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[380px] space-y-7">
          <img
            src="/logo-wordmark.svg"
            alt="CargoFL"
            draggable={false}
            className="h-[26px] w-auto object-contain object-left dark:brightness-0 dark:invert"
          />

          <WaveArt className="-ml-3 h-20 w-auto" />
          <div className="space-y-1.5">
            <h1 className="text-2xl font-bold tracking-[-0.02em]">Welcome back</h1>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {isPending
                ? 'Checking how this server is configured…'
                : isError
                  ? 'The sign-in service is not responding.'
                  : config?.googleClientId
                    ? 'Sign in with your work Google account to reach your timesheet.'
                    : 'Sign in with your work email and password to reach your timesheet.'}
            </p>
          </div>

          <div className="space-y-4">
            {/*
              Three distinct states, deliberately not collapsed into one message.
              "Cannot reach the API" and "the API says sign-in is unconfigured" have
              different fixes - restart a container versus set an env var - and
              reporting a 502 as "not configured" sends whoever is on call to the
              wrong place.
            */}
            {config?.googleClientId ? (
              <GoogleButton clientId={config.googleClientId} onSignedIn={onSignedIn} />
            ) : isError ? (
              <Alert className="border-destructive/30">
                <AlertDescription className="text-xs leading-relaxed">
                  Cannot reach the sign-in service at <code className="font-mono">{API_URL}</code>.
                  It may be restarting — try again in a moment. If this persists, the API is
                  down rather than misconfigured.
                </AlertDescription>
              </Alert>
            ) : (
              !isPending &&
              !config?.passwordLoginEnabled && (
                <Alert>
                  <AlertDescription className="text-xs leading-relaxed">
                    The server is running but no sign-in method is configured on it. An
                    administrator needs to set{' '}
                    <code className="font-mono">GOOGLE_CLIENT_ID</code> and restart it.
                  </AlertDescription>
                </Alert>
              )
            )}

            {config?.passwordLoginEnabled && (
              <PasswordLogin
                config={config}
                withDivider={Boolean(config.googleClientId)}
                onSignedIn={onSignedIn}
              />
            )}
            {config?.devLoginEnabled && <DevLogin config={config} onSignedIn={onSignedIn} />}
          </div>

          <p className="text-xs leading-relaxed text-muted-foreground">
            No account yet? HR creates employee records — accounts are not created on first
            sign-in, so hours are never booked against a person nobody added.
          </p>
        </div>
      </div>

      {/* Hidden below lg: on a phone it would push the sign-in button off the first
          screen, which is the one thing this page has to do. */}
      <div className="relative hidden overflow-hidden bg-sidebar px-12 py-12 lg:flex lg:flex-col lg:justify-center">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden opacity-[0.22]"
          style={{
            maskImage: 'radial-gradient(75% 60% at 50% 45%, black, transparent 78%)',
            WebkitMaskImage: 'radial-gradient(75% 60% at 50% 45%, black, transparent 78%)',
          }}
        >
          <div
            className="ts-grid absolute -inset-y-24 inset-x-0"
            style={{
              backgroundImage:
                'linear-gradient(hsl(var(--sidebar-border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--sidebar-border)) 1px, transparent 1px)',
              backgroundSize: '44px 44px',
            }}
          />
        </div>

        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            className="ts-aurora absolute -right-1/4 -top-1/3 size-[46rem] rounded-full"
            style={{
              background:
                'radial-gradient(circle, hsl(var(--sidebar-primary) / 0.28), transparent 62%)',
            }}
          />
          <div
            className="ts-aurora-alt absolute -bottom-1/3 -left-1/4 size-[42rem] rounded-full"
            style={{
              background: 'radial-gradient(circle, hsl(var(--sidebar-accent) / 0.85), transparent 65%)',
            }}
          />
        </div>

        <div className="relative max-w-[440px] space-y-7">
          <span className="ts-rise inline-flex items-center gap-2 rounded-full border border-sidebar-border bg-sidebar-accent/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-sidebar-foreground">
            <span className="relative flex size-1.5">
              <span className="ts-halo absolute inset-0 rounded-full bg-sidebar-primary" />
              <span className="relative size-1.5 rounded-full bg-sidebar-primary" />
            </span>
            CargoFL Timesheet
          </span>

          <h2
            className="ts-rise bg-gradient-to-br from-white via-white to-sidebar-foreground/55 bg-clip-text text-3xl font-bold leading-[1.15] tracking-[-0.02em] text-transparent"
            style={{ animationDelay: '90ms' }}
          >
            Your team&rsquo;s time, accounted for.
          </h2>

          <p
            className="ts-rise text-sm leading-relaxed text-sidebar-foreground/80"
            style={{ animationDelay: '180ms' }}
          >
            Hours, absences and working contracts in one place — with a week that closes
            cleanly and a trail behind every approval.
          </p>

          <ul className="space-y-3">
            {FEATURES.map((feature, i) => (
              <li
                key={feature.title}
                className="ts-rise -mx-3 flex gap-3 rounded-lg border border-transparent px-3 py-2.5 transition-colors duration-300 hover:border-sidebar-border hover:bg-sidebar-accent/40"
                style={{ animationDelay: `${280 + i * 90}ms` }}
              >
                <span className="relative mt-[7px] flex size-1.5 shrink-0">
                  <span
                    className="ts-halo absolute inset-0 rounded-full bg-sidebar-primary"
                    style={{ animationDelay: `${i * 900}ms` }}
                  />
                  <span className="relative size-1.5 rounded-full bg-sidebar-primary" />
                </span>
                <div className="space-y-0.5">
                  <div className="text-sm font-semibold text-white">{feature.title}</div>
                  <div className="text-[13px] leading-relaxed text-sidebar-foreground/70">
                    {feature.blurb}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/** The three things that distinguish this from a spreadsheet. */
const FEATURES = [
  {
    title: 'A week that closes',
    blurb: 'Submit Monday to Sunday; approved weeks lock, and stay locked',
  },
  {
    title: 'Contracts do the arithmetic',
    blurb: 'Expected hours, leave entitlement and holidays, per person',
  },
  {
    title: 'Every approval leaves a trail',
    blurb: 'Who signed off which week, and when — never recomputed after the fact',
  },
];

/**
 * Email and password.
 *
 * Verifies a bcrypt hash, so unlike the development box below this is offered in
 * production. The failure message is whatever the API said, which is deliberately
 * the same sentence for every cause - naming the cause would tell somebody whether
 * an address exists.
 */
function PasswordLogin({
  config,
  withDivider,
  onSignedIn,
}: {
  config: AuthConfig;
  withDivider: boolean;
  onSignedIn: () => Promise<void>;
}) {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/password', { email, password });
      await onSignedIn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign-in failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {withDivider && (
        <div className="relative py-1">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-background px-2 text-[11px] uppercase tracking-wide text-muted-foreground">
              or with your password
            </span>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="login-email">Work email</Label>
          <Input
            id="login-email"
            type="email"
            autoComplete="username"
            placeholder={
              config.allowedEmailDomain ? `you@${config.allowedEmailDomain}` : 'you@work.com'
            }
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="login-password">Password</Label>
          <Input
            id="login-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        {error && (
          <Alert className="border-destructive/30">
            <AlertDescription className="text-xs leading-relaxed">{error}</AlertDescription>
          </Alert>
        )}
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          {/* Points at Google rather than at a password reset. Password sign-in is
              being retired, so promising a reset would send people down the route
              that is going away - and there is deliberately no screen for it. */}
          The same password you used before. If you have forgotten it, sign in with
          Google instead.
        </p>
      </form>
    </>
  );
}

function DevLogin({
  config,
  onSignedIn,
}: {
  config: AuthConfig;
  onSignedIn: () => Promise<void>;
}) {
  const [email, setEmail] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/dev-login', { email });
      await onSignedIn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign-in failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="relative py-1">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-background px-2 text-[11px] uppercase tracking-wide text-muted-foreground">
            Development only
          </span>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="dev-email">Sign in as</Label>
          <Input
            id="dev-email"
            type="email"
            autoComplete="off"
            placeholder={
              config.allowedEmailDomain
                ? `someone@${config.allowedEmailDomain}`
                : 'someone@your-company.com'
            }
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        {error && (
          <Alert className="border-destructive/30">
            <AlertDescription className="text-xs">{error}</AlertDescription>
          </Alert>
        )}
        <Button type="submit" variant="secondary" className="w-full" disabled={busy}>
          {busy ? 'Signing in…' : 'Continue without a password'}
        </Button>
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          No password is checked. This box exists only because the server was started with{' '}
          <code className="font-mono">AUTH_DEV_LOGIN=true</code>, which the API refuses to
          start with in production. It can only assume an identity that already exists.
        </p>
      </form>
    </>
  );
}

/**
 * Google Identity Services, loaded on demand — here rather than in index.html so a
 * deployment with no client id never fetches Google, and the button cannot render
 * before the id it needs is known.
 */
function GoogleButton({
  clientId,
  onSignedIn,
}: {
  clientId: string;
  onSignedIn: () => Promise<void>;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [failed, setFailed] = React.useState(false);
  const [rejected, setRejected] = React.useState<string | null>(null);

  React.useEffect(() => {
    const handleCredential = async (response: { credential?: string }) => {
      if (!response.credential) return;
      try {
        await api.post('/auth/google', { idToken: response.credential });
        await onSignedIn();
      } catch (error) {
        setRejected(
          error instanceof ApiError ? error.message : 'Google sign-in was not accepted.',
        );
      }
    };

    const render = () => {
      const google = (window as unknown as { google?: GoogleAccounts }).google;
      if (!google || !ref.current) return;
      google.accounts.id.initialize({
        client_id: clientId,
        callback: (r) => void handleCredential(r),
      });
      google.accounts.id.renderButton(ref.current, {
        theme: 'outline',
        size: 'large',
        width: 380,
        text: 'signin_with',
      });
    };

    if (document.getElementById('google-identity')) {
      render();
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-identity';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = render;
    // An ad blocker or an offline machine otherwise leaves a permanent blank
    // rectangle with nothing on screen saying why.
    script.onerror = () => setFailed(true);
    document.head.appendChild(script);
  }, [clientId, onSignedIn]);

  if (failed) {
    return (
      <Alert>
        <AlertDescription className="text-xs">
          Google&rsquo;s sign-in script could not be loaded. Check your connection, or
          whether an extension is blocking accounts.google.com.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      <div ref={ref} className="flex min-h-[44px] justify-center" />
      {rejected && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-xs leading-relaxed">{rejected}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}

interface GoogleAccounts {
  accounts: {
    id: {
      initialize: (options: {
        client_id: string;
        callback: (response: { credential?: string }) => void;
      }) => void;
      renderButton: (
        parent: HTMLElement,
        options: { theme: string; size: string; width: number; text: string },
      ) => void;
    };
  };
}
